'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { TabsRootContext } from './context/tabs-root.context';
import { tabsListRecipe } from './styles/tabs-list.styles';
import type { TabsListPropsInterface } from './types/tabs-list.types';

/**
 * A `Tabs.List` is a collection of `Tabs.Tab` components. Similiar to `Radio` controls,
 * the tabs in a `Tabs.List` are **mutually exclusive** -- only one `Tabs.Tab` can be
 * selected at any given time.
 *
 * @extends ToggleButtonGroup
 * @since 8.10.0
 */

export const TabsList = forwardRef<HTMLDivElement, TabsListPropsInterface>(
  (props: TabsListPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;

    const { appearance, fluid = false } = useContext(TabsRootContext);

    const styles = css(tabsListRecipe.raw({ appearance, fluid }), cssProp);

    return (
      <div
        role="tablist"
        ref={forwardedRef}
        data-c11n-component="Tabs.List"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

TabsList.displayName = 'Tabs.List';
